<script setup lang="ts">
import CardComponent from '@/components/cards/CardComponent.vue'
import HeadingDefault from '@/components/Heading/HeadingDefault.vue'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import SelectOptionPrime from '@/components/forms/SelectOption/SelectOptionPrime.vue'
import { ref, reactive, onMounted, computed, watch } from 'vue'
import TextAreaDefault from '@/components/forms/TextArea/TextAreaDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import ReportColumnSelectionModal from '../Modals/ReportColumnSelectionModal.vue'
import { notificationService } from '@/services/notificationService'
import { tenderReportService, type TenderReportData } from '@/services/tenderReportService'
import { HardwareInstallationReportTemplateGenerator } from '@/utils/templateGenerator'
import api from '@/services/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

import {
  TrashIcon,
  PlusIcon,
  SaveIcon,
  BuildingIcon,
  EngineerIcon,
  FileWaveIcon
} from '@/icons'
import FilePdfIcon from '@/icons/FilePdfIcon.vue'
import LoaderDefault from '@/components/common/LoaderDefault.vue'

// ========== TYPE DEFINITIONS ==========
interface Engineer {
  id: string
  first_name: string
  last_name: string
  email: string
  username?: string
  phone?: string
  department?: string
}

interface WonTender {
  id: string
  t247Id?: string
  referenceNo?: string
  tenderBrief?: string
  customerName?: string
  customerAddress?: string
  organization?: string
  company?: string
  workflowStatus?: string
}

interface ReportDataToSave {
  id?: string;
  tenderId: string;
  selectedTender?: string;
  t247Id?: string;
  company?: string;
  referenceNumber?: string;
  purchaseOrderNumber?: string;
  reportDate?: string | null;
  poDate?: string | null;
  customerName?: string;
  customerAddress?: string;
  stateOffice?: string;
  contactPersonName?: string;
  contactPersonDesignation?: string;
  contactPersonNumber?: string;
  department?: string;
  consignees: Array<{
    name: string;
    designation: string;
    number: string;
    assignedEngineerId: string;
    assignedEngineerName: string;
    callAlignedDate: string | null;
    emailId?: string;
    faxNumber?: string;
    reportCards: Array<{
      title: string;
      itemName: string;
      quantity: number;
      visibleFields: Record<string, boolean>;
      itemGroups: Array<{
        fieldValues: Record<string, string>;
      }>;
      warrantyYears?: number;
      warrantyMonths?: number;
    }>;
  }>;
  reportTitle: string;
}

interface Consignee {
  id: number
  name: string
  designation: string
  number: string
  assignedEngineerId: string
  assignedEngineerName: string
  callAlignedDate: string
  emailId: string
  faxNumber: string
  reportCards: ReportCard[]
}

interface ItemGroup {
  id: number
  fieldValues: Record<string, string>
}

interface ReportCard {
  id: number
  title: string
  itemName: string
  quantity: number
  visibleFields: Record<string, boolean>
  itemGroups: ItemGroup[]
  warrantyYears?: number
  warrantyMonths?: number
}

interface ExistingReport {
  id?: string
  tenderId: string
  selectedTender?: string
  t247Id?: string
  company?: string
  referenceNumber?: string
  purchaseOrderNumber?: string
  reportDate?: string
  poDate?: string
  customerName?: string
  customerAddress?: string
  stateOffice?: string
  contactPersonName?: string
  contactPersonDesignation?: string
  contactPersonNumber?: string
  department?: string
  consignees?: ExistingReportConsignee[]
}

interface ExistingReportConsignee {
  name?: string
  designation?: string
  number?: string
  assignedEngineerId?: string
  assignedEngineerName?: string
  callAlignedDate?: string
  emailId?: string
  faxNumber?: string
  reportCards?: ExistingReportCard[]
}

interface ExistingReportCard {
  title?: string
  itemName?: string
  quantity?: number
  visibleFields?: Record<string, boolean>
  itemGroups?: ExistingReportItemGroup[]
  warrantyYears?: number
  warrantyMonths?: number
}

interface ExistingReportItemGroup {
  fieldValues: Record<string, string>
}

// API Response Types
interface TenderApiResponse {
  success?: boolean
  tenders?: TenderApiItem[]
  data?: TenderApiItem[]
}

interface TenderApiItem {
  id: string
  t247Id?: string
  tenderId?: string
  referenceNo?: string
  referenceNumber?: string
  tenderBrief?: string
  description?: string
  customerName?: string
  clientName?: string
  customerAddress?: string
  siteAddress?: string
  organization?: string
  company?: string
  workflowStatus?: string
}

interface EngineerApiResponse {
  success?: boolean
  engineers?: EngineerApiItem[]
}

interface EngineerApiItem {
  id: string | number
  first_name?: string
  last_name?: string
  email?: string
  username?: string
  phone?: string
  department?: string
}

// ========== STATE ==========
const date = ref<string>('')
const showModal = ref<boolean>(false)
const activeConsigneeIndex = ref<number | null>(null)
const activeCardIndex = ref<number | null>(null)
const modalRef = ref<InstanceType<typeof ReportColumnSelectionModal> | null>(null)
const isSaving = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const isGeneratingPDF = ref<boolean>(false)

// ── Consignee selection modal (shown when >1 consignee before PDF download) ──
const showConsigneeSelectModal = ref<boolean>(false)
// null = all consignees, number = specific consignee id
const selectedConsigneeId = ref<number | null>(null)

// Copy consignee
const copyFromDetails = ref<Record<number, boolean>>({})

// Engineers list
const engineers = ref<Engineer[]>([])
const isLoadingEngineers = ref<boolean>(false)

// Won tenders list
const wonTenders = ref<WonTender[]>([])
const isLoadingTenders = ref<boolean>(false)

// Form data
const formData = reactive({
  selectedTender: '',
  tenderId: '',
  t247Id: '',
  company: '',

  referenceNumber: '',
  purchaseOrderNumber: '',
  poDate: '',
  customerName: '',
  customerAddress: '',
  stateOffice: '',
  contactPersonName: '',
  contactPersonDesignation: '',
  contactPersonNumber: '',
  department: '',

  consignees: [] as Consignee[],
})

// Track existing report ID
const existingReportId = ref<string | undefined>(undefined)

// ID counters
let nextConsigneeId = 1000
let nextCardId = 10000
let nextGroupId = 20000

const generateCardId = () => nextCardId++
const generateGroupId = () => nextGroupId++

// Warranty options
const warrantyYearsOptions = computed(() => {
  const options: Array<{ label: string; value: number | undefined }> = [{ label: 'Select Year', value: undefined }]
  for (let i = 1; i <= 10; i++) options.push({ label: `${i} Year${i > 1 ? 's' : ''}`, value: i })
  return options
})

const warrantyMonthsOptions = computed(() => {
  const options: Array<{ label: string; value: number | undefined }> = [{ label: 'Select Month', value: undefined }]
  for (let i = 1; i <= 11; i++) options.push({ label: `${i} Month${i > 1 ? 's' : ''}`, value: i })
  return options
})

// ── Computed: consignees that have at least one report card ──────────────────
const consigneesWithReports = computed(() =>
  formData.consignees.filter(c => c.reportCards.length > 0)
)

const selectConsignee = (id: number | null) => {
  selectedConsigneeId.value = id
}

// Function to copy details from main form to consignee
const copyDetailsToConsignee = (consigneeId: number) => {
  const consignee = formData.consignees.find(c => c.id === consigneeId)
  if (consignee) {
    consignee.name = formData.contactPersonName
    consignee.designation = formData.contactPersonDesignation
    consignee.number = formData.contactPersonNumber
  }
}

// Watch for changes in the checkbox
const handleCopyCheckboxChange = (consigneeId: number, checked: boolean) => {
  copyFromDetails.value[consigneeId] = checked
  if (checked) {
    copyDetailsToConsignee(consigneeId)
  }
}

// Watch for changes in main form fields to auto-update when checkbox is checked
watch([() => formData.contactPersonName, () => formData.contactPersonDesignation, () => formData.contactPersonNumber], () => {
  formData.consignees.forEach(consignee => {
    if (copyFromDetails.value[consignee.id]) {
      copyDetailsToConsignee(consignee.id)
    }
  })
})

// ========== API FUNCTIONS ==========
const fetchWonTenders = async (): Promise<void> => {
  try {
    isLoadingTenders.value = true
    const response = await api.get('/api/tenders/won')
    let tendersData: TenderApiItem[] = []
    const data = response.data as TenderApiResponse

    if (data.success && data.tenders) tendersData = data.tenders
    else if (data.data && Array.isArray(data.data)) tendersData = data.data
    else if (Array.isArray(response.data)) tendersData = response.data

    wonTenders.value = tendersData.map((tender: TenderApiItem) => ({
      id: tender.id,
      t247Id: tender.t247Id || tender.tenderId,
      referenceNo: tender.referenceNo || tender.referenceNumber,
      tenderBrief: tender.tenderBrief || tender.description || '',
      customerName: tender.customerName || tender.clientName || '',
      customerAddress: tender.customerAddress || tender.siteAddress || '',
      organization: tender.organization || '',
      company: tender.company || '',
      workflowStatus: tender.workflowStatus || 'won'
    }))
  } catch (error) {
    console.error('Error fetching won tenders:', error)
    notificationService.error('Failed to fetch won tenders')
    wonTenders.value = []
  } finally {
    isLoadingTenders.value = false
  }
}

const fetchEngineers = async (): Promise<void> => {
  try {
    isLoadingEngineers.value = true
    const response = await api.get('/api/users/engineers')
    const data = response.data as EngineerApiResponse

    if (data.success && data.engineers) {
      engineers.value = data.engineers.map((eng: EngineerApiItem) => ({
        id: eng.id.toString(),
        first_name: eng.first_name || '',
        last_name: eng.last_name || '',
        email: eng.email || '',
        username: eng.username || '',
        phone: eng.phone || '',
        department: eng.department || ''
      }))
    }
  } catch (error) {
    console.error('Error fetching engineers:', error)
    notificationService.error('Failed to fetch engineers')
    engineers.value = []
  } finally {
    isLoadingEngineers.value = false
  }
}

// ========== TOTAL ITEM COUNT ==========
const totalItemsAcrossAllReports = computed(() => {
  let total = 0
  formData.consignees.forEach(consignee => {
    consignee.reportCards.forEach(card => { total += card.itemGroups.length })
  })
  return total
})

// ========== ITEM GROUP MANAGEMENT ==========
const addItemGroup = (consigneeIndex: number, cardIndex: number): void => {
  const consignee = formData.consignees[consigneeIndex]
  if (!consignee) return
  const card = consignee.reportCards[cardIndex]
  if (!card) return

  const fieldValues: Record<string, string> = {}
  Object.keys(card.visibleFields).forEach(key => {
    if (card.visibleFields[key]) fieldValues[key] = ''
  })

  card.itemGroups.push({ id: generateGroupId(), fieldValues })
  formData.consignees = [...formData.consignees]
}

const removeItemGroup = (consigneeIndex: number, cardIndex: number, groupId: number): void => {
  const consignee = formData.consignees[consigneeIndex]
  if (!consignee) return
  const card = consignee.reportCards[cardIndex]
  if (!card) return

  if (card.itemGroups.length <= 1) {
    notificationService.warning('At least one item group is required')
    return
  }
  card.itemGroups = card.itemGroups.filter(g => g.id !== groupId)
  formData.consignees = [...formData.consignees]
}

// ========== FORM FUNCTIONS ==========
const clearFormData = (): void => {
  formData.selectedTender = ''
  formData.tenderId = ''
  formData.t247Id = ''
  formData.company = ''
  formData.referenceNumber = ''
  formData.purchaseOrderNumber = ''
  formData.poDate = ''
  formData.customerName = ''
  formData.customerAddress = ''
  formData.stateOffice = ''
  formData.contactPersonName = ''
  formData.contactPersonDesignation = ''
  formData.contactPersonNumber = ''
  formData.department = ''
  formData.consignees = []
  date.value = ''
  existingReportId.value = undefined
}

// ========== TENDER SELECTION ==========
const handleTenderChange = async (value: string): Promise<void> => {
  formData.selectedTender = value
  if (!value) { clearFormData(); return }

  const selectedTender = wonTenders.value.find(t => t.t247Id === value || t.id === value)
  if (selectedTender) {
    formData.tenderId = selectedTender.id
    formData.t247Id = selectedTender.t247Id || value
    formData.company = selectedTender.company || ''
    if (selectedTender.customerName) formData.customerName = selectedTender.customerName
    if (selectedTender.customerAddress) formData.customerAddress = selectedTender.customerAddress
    if (selectedTender.referenceNo) formData.referenceNumber = selectedTender.referenceNo

    if (formData.tenderId) {
      const hasReport = await loadExistingReport(formData.tenderId)
      if (!hasReport) {
        formData.consignees = [{
          id: nextConsigneeId++, name: '', designation: '', number: '',
          assignedEngineerId: '', assignedEngineerName: '', callAlignedDate: '',
          emailId: '', faxNumber: '', reportCards: []
        }]
        formData.purchaseOrderNumber = ''
        formData.poDate = ''
        formData.customerName = ''
        formData.customerAddress = ''
        formData.stateOffice = ''
        formData.contactPersonName = ''
        formData.contactPersonDesignation = ''
        formData.contactPersonNumber = ''
        formData.department = ''
      }
    }
  }
}

// ========== CONSIGNEE MANAGEMENT ==========
const addConsignee = (): void => {
  formData.consignees.push({
    id: nextConsigneeId++, name: '', designation: '', number: '',
    assignedEngineerId: '', assignedEngineerName: '', callAlignedDate: '',
    emailId: '', faxNumber: '', reportCards: []
  })
}

const removeConsignee = (consigneeId: number): void => {
  if (formData.consignees.length > 1) {
    formData.consignees = formData.consignees.filter(c => c.id !== consigneeId)
  } else {
    notificationService.warning('At least one consignee is required')
  }
}

const updateConsigneeEngineer = (consignee: Consignee, engineerId: string): void => {
  consignee.assignedEngineerId = engineerId
  const engineer = engineers.value.find(e => e.id === engineerId)
  consignee.assignedEngineerName = engineer
    ? `${engineer.first_name || ''} ${engineer.last_name || ''}`.trim() || engineer.email
    : ''
}

// ========== REPORT CARD MANAGEMENT ==========
const openAddTemplateModal = (consigneeIndex: number): void => {
  activeConsigneeIndex.value = consigneeIndex
  activeCardIndex.value = formData.consignees[consigneeIndex].reportCards.length
  showModal.value = true
}

const handleModalSave = (selectedFields: Record<string, boolean>, quantity: number): void => {
  if (activeConsigneeIndex.value !== null && activeCardIndex.value !== null) {
    const consignee = formData.consignees[activeConsigneeIndex.value]
    if (activeCardIndex.value === consignee.reportCards.length) {
      const visibleFields = {
        monitorSerialNumber: false, cpuSerialNumber: false,
        serialNumberCommon: false, installationStatus: false, remarks: false,
        ...selectedFields
      }
      type VFK = keyof typeof visibleFields
      const itemGroups: ItemGroup[] = Array.from({ length: quantity }, () => ({
        id: generateGroupId(),
        fieldValues: Object.fromEntries(
          (Object.keys(visibleFields) as VFK[]).filter(k => visibleFields[k]).map(k => [k, ''])
        )
      }))
      consignee.reportCards.push({
        id: generateCardId(),
        title: `Report Template ${consignee.reportCards.length + 1}`,
        itemName: '', quantity, visibleFields, itemGroups,
        warrantyYears: undefined,
        warrantyMonths: undefined
      })
      notificationService.success(`Report template added with ${quantity} item(s) for ${consignee.name || 'Consignee'}`)
    }
  }
  showModal.value = false
  activeConsigneeIndex.value = null
  activeCardIndex.value = null
}

const handleModalCancel = (): void => {
  showModal.value = false
  activeConsigneeIndex.value = null
  activeCardIndex.value = null
}

const handleModalSaveClick = async (): Promise<void> => {
  if (modalRef.value) modalRef.value.save()
}

const removeReportCard = (consigneeIndex: number, cardId: number): void => {
  const consignee = formData.consignees[consigneeIndex]
  if (consignee.reportCards.length > 1) {
    consignee.reportCards = consignee.reportCards.filter(card => card.id !== cardId)
  } else {
    notificationService.warning('At least one report card is required per consignee')
  }
}

const updateFieldValue = (consigneeIndex: number, cardIndex: number, groupId: number, fieldName: string, value: string | Date): void => {
  const card = formData.consignees[consigneeIndex]?.reportCards[cardIndex]
  if (!card) return
  const group = card.itemGroups.find(g => g.id === groupId)
  if (!group) return
  group.fieldValues[fieldName] = String(value)
  formData.consignees = [...formData.consignees]
}

const updateItemName = (consigneeIndex: number, cardIndex: number, value: string | Date): void => {
  const card = formData.consignees[consigneeIndex]?.reportCards[cardIndex]
  if (card) { card.itemName = String(value); formData.consignees = [...formData.consignees] }
}

// ========== LOAD EXISTING REPORT ==========
const FORM_FIELDS = [
  'selectedTender', 't247Id', 'company', 'referenceNumber',
  'purchaseOrderNumber', 'poDate', 'customerName', 'customerAddress',
  'stateOffice', 'contactPersonName', 'contactPersonDesignation',
  'contactPersonNumber', 'department',
] as const

const loadExistingReport = async (tenderId: string): Promise<boolean> => {
  try {
    isLoading.value = true
    const existingReport = await tenderReportService.getReportByTenderId(tenderId) as ExistingReport | null

    if (existingReport) {
      existingReportId.value = existingReport.id

      for (const key of FORM_FIELDS) {
        if (existingReport[key] !== undefined) {
          (formData as Record<string, unknown>)[key] = existingReport[key] as string || ''
        }
      }

      if (existingReport.reportDate) {
        const dateStr = existingReport.reportDate.toString()
        if (dateStr !== 'Invalid date' && dateStr !== '') date.value = dateStr.split('T')[0]
      }

      if (existingReport.consignees && existingReport.consignees.length > 0) {
        formData.consignees = existingReport.consignees.map((c: ExistingReportConsignee) => {
          let formattedDate = ''
          if (c.callAlignedDate) {
            try {
              const dateObj = new Date(c.callAlignedDate)
              if (!isNaN(dateObj.getTime())) formattedDate = dateObj.toISOString().split('T')[0]
            } catch (e) { console.error('Error formatting date:', e) }
          }

          return {
            id: nextConsigneeId++,
            name: c.name || '', designation: c.designation || '', number: c.number || '',
            assignedEngineerId: c.assignedEngineerId || '',
            assignedEngineerName: c.assignedEngineerName || '',
            callAlignedDate: formattedDate,
            emailId: c.emailId || '', faxNumber: c.faxNumber || '',
            reportCards: (c.reportCards || []).map((card: ExistingReportCard) => {
              const visibleFields = card.visibleFields || {
                monitorSerialNumber: false, cpuSerialNumber: false,
                serialNumberCommon: false, installationStatus: false, remarks: false
              }
              const quantity = card.quantity || 1
              const itemGroups: ItemGroup[] = (card.itemGroups || []).map((group: ExistingReportItemGroup) => ({
                id: generateGroupId(), fieldValues: { ...group.fieldValues }
              }))
              while (itemGroups.length < quantity) {
                const fieldValues: Record<string, string> = {}
                Object.keys(visibleFields).forEach(key => { if (visibleFields[key]) fieldValues[key] = '' })
                itemGroups.push({ id: generateGroupId(), fieldValues })
              }
              return { id: generateCardId(), title: card.title || 'Report Template', itemName: card.itemName || '', quantity, visibleFields, itemGroups, warrantyYears: card.warrantyYears, warrantyMonths: card.warrantyMonths }
            })
          }
        })
      } else {
        formData.consignees = [{
          id: nextConsigneeId++, name: '', designation: '', number: '',
          assignedEngineerId: '', assignedEngineerName: '', callAlignedDate: '',
          emailId: '', faxNumber: '', reportCards: []
        }]
      }

      notificationService.success('Report loaded successfully')
      return true
    }
    return false
  } catch (error) {
    console.error('Error loading report:', error)
    notificationService.error('Failed to load existing report')
    return false
  } finally {
    isLoading.value = false
  }
}

// ========== SAVE REPORT ==========
const saveReport = async (): Promise<void> => {
  if (!formData.selectedTender) {
    notificationService.warning('Please select a tender first');
    return;
  }
  if (!formData.consignees.some(c => c.reportCards.length > 0)) {
    notificationService.warning('Please add at least one report template for any consignee');
    return;
  }

  try {
    isSaving.value = true

    const validateDate = (dateValue: unknown): string | null => {
      if (!dateValue || dateValue === 'Invalid date' || dateValue === '') return null
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue))
        return new Date(dateValue).toISOString()
      try {
        const d = new Date(String(dateValue))
        return isNaN(d.getTime()) ? null : d.toISOString()
      } catch { return null }
    }

    // Prepare report data - only include id if we're updating an existing report
    const reportData: ReportDataToSave = {
      tenderId: formData.tenderId,
      selectedTender: formData.selectedTender || undefined,
      t247Id: formData.t247Id || undefined,
      company: formData.company || undefined,
      referenceNumber: formData.referenceNumber || undefined,
      purchaseOrderNumber: formData.purchaseOrderNumber || undefined,
      reportDate: validateDate(date.value),
      poDate: validateDate(formData.poDate),
      customerName: formData.customerName || undefined,
      customerAddress: formData.customerAddress || undefined,
      stateOffice: formData.stateOffice || undefined,
      contactPersonName: formData.contactPersonName || undefined,
      contactPersonDesignation: formData.contactPersonDesignation || undefined,
      contactPersonNumber: formData.contactPersonNumber || undefined,
      department: formData.department || undefined,
      consignees: formData.consignees.map(c => ({
        name: c.name, designation: c.designation, number: c.number,
        assignedEngineerId: c.assignedEngineerId, assignedEngineerName: c.assignedEngineerName,
        callAlignedDate: validateDate(c.callAlignedDate),
        emailId: c.emailId || undefined, faxNumber: c.faxNumber || undefined,
        reportCards: c.reportCards.map(card => ({
          title: card.title, itemName: card.itemName, quantity: card.quantity,
          visibleFields: { ...card.visibleFields },
          itemGroups: card.itemGroups.map(group => ({ fieldValues: { ...group.fieldValues } })),
          warrantyYears: card.warrantyYears,
          warrantyMonths: card.warrantyMonths
        }))
      })),
      reportTitle: `Report for ${formData.t247Id || formData.selectedTender}`
    }

    // Only add id if we're updating an existing report
    if (existingReportId.value) {
      reportData.id = existingReportId.value
    }

    const response = await tenderReportService.saveReport(reportData as unknown as TenderReportData)
    if (response?.id) {
      existingReportId.value = response.id
      await loadExistingReport(formData.tenderId)
      notificationService.success('Report updated successfully')
    }
  } catch (error) {
    console.error('❌ Error saving report:', error)
    notificationService.error('Failed to save report')
  } finally {
    isSaving.value = false
  }
}
// ========== PDF GENERATION ==========

// Core function that actually builds and downloads the PDF for a given consignee list
const doGeneratePDF = async (consigneesToInclude: Consignee[]): Promise<void> => {
  const visibleFieldsConfig: Record<string, boolean> = {
    monitorSerialNumber: false, cpuSerialNumber: false,
    serialNumberCommon: false, installationStatus: false, remarks: false
  }
  consigneesToInclude.forEach(consignee => {
    consignee.reportCards.forEach(card => {
      Object.keys(card.visibleFields).forEach(key => {
        if (card.visibleFields[key]) visibleFieldsConfig[key] = true
      })
    })
  })

  const pdfData = {
    company: formData.company || '',
    selectedCompanyProfile: formData.company || '',
    companyProfileName: formData.company || '',
    companyAddress: formData.customerAddress || '',
    tenderRefNo: formData.referenceNumber || formData.t247Id,
    date: date.value,
    signatoryName: formData.contactPersonName || '',
    designation: formData.contactPersonDesignation || '',
    signatureDate: date.value,
    purchaseOrderNumber: formData.purchaseOrderNumber || '',
    poDate: formData.poDate || '',
    customerName: formData.customerName || '',
    customerAddress: formData.customerAddress || '',
    stateOffice: formData.stateOffice || '',
    contactPersonName: formData.contactPersonName || '',
    contactPersonDesignation: formData.contactPersonDesignation || '',
    contactPersonNumber: formData.contactPersonNumber || '',
    department: formData.department || '',
    consignees: consigneesToInclude.map(c => ({
      name: c.name, designation: c.designation, number: c.number,
      assignedEngineerId: c.assignedEngineerId, assignedEngineerName: c.assignedEngineerName,
      callAlignedDate: c.callAlignedDate, emailId: c.emailId, faxNumber: c.faxNumber,
      reportCards: c.reportCards.map(card => ({
        id: card.id, title: card.title, itemName: card.itemName, quantity: card.quantity,
        visibleFields: card.visibleFields,
        warrantyYears: card.warrantyYears,
        warrantyMonths: card.warrantyMonths,
        itemGroups: card.itemGroups.map(group => ({
          fieldValues: {
            monitorSerialNumber: group.fieldValues.monitorSerialNumber || '',
            cpuSerialNumber: group.fieldValues.cpuSerialNumber || '',
            serialNumberCommon: group.fieldValues.serialNumberCommon || '',
            installationStatus: group.fieldValues.installationStatus || '',
            remarks: group.fieldValues.remarks || ''
          }
        }))
      }))
    })),
    visibleFieldsConfig
  }

  await HardwareInstallationReportTemplateGenerator.generateHardwareInstallationPDF(pdfData)
}

// Entry point — shows selection modal if >1 consignee, else downloads directly
const generatePDF = async (): Promise<void> => {
  if (!formData.selectedTender) { notificationService.warning('Please select a tender first'); return }
  if (!formData.consignees.some(c => c.reportCards.length > 0)) {
    notificationService.warning('Please add at least one report template for any consignee'); return
  }
  if (isGeneratingPDF.value) return

  // Single consignee — download directly without showing any modal
  if (consigneesWithReports.value.length <= 1) {
    try {
      isGeneratingPDF.value = true
      await doGeneratePDF(consigneesWithReports.value)
      notificationService.success('PDF generated successfully')
    } catch (error) {
      console.error('Error generating PDF:', error)
      notificationService.error('Failed to generate PDF')
    } finally {
      isGeneratingPDF.value = false
    }
    return
  }

  // Multiple consignees — open selection modal
  // Multiple consignees — open selection modal, pre-select "all"
  selectedConsigneeId.value = null
  showConsigneeSelectModal.value = true
}

// Called when user confirms their selection in the modal
const confirmConsigneeSelection = async (): Promise<void> => {
  showConsigneeSelectModal.value = false

  const toInclude = selectedConsigneeId.value === null
    ? consigneesWithReports.value
    : consigneesWithReports.value.filter(c => c.id === selectedConsigneeId.value)

  if (toInclude.length === 0) {
    notificationService.warning('Please select a consignee')
    return
  }

  try {
    isGeneratingPDF.value = true
    await doGeneratePDF(toInclude)
    const label = toInclude.length === 1
      ? `Report for ${toInclude[0].name || 'Consignee'}`
      : `Report for ${toInclude.length} consignees`
    notificationService.success(`${label} generated successfully`)
  } catch (error) {
    console.error('Error generating PDF:', error)
    notificationService.error('Failed to generate PDF')
  } finally {
    isGeneratingPDF.value = false
  }
}

// ========== INITIALIZATION ==========
onMounted(() => {
  fetchWonTenders()
  fetchEngineers()
})

import { useSidebar } from '@/composables/useSidebar'
import CheckBoxDefault from '@/components/forms/Checkboxes/CheckBoxDefault.vue'
const { isExpanded, isHovered } = useSidebar()
</script>

<template>
  <AdminLayout customClasses="mb-25">
    <HeadingDefault heading="Reports Management" />

    <!-- Fixed Action Bar -->
    <div
      :class="['flex justify-between gap-3 mb-0 p-4 md:p-6 fixed bottom-0 right-0 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 z-60', isExpanded || isHovered ? 'lg:left-(--sidebar-max) lg:max-w-[calc(100%_-_var(--sidebar-max))]' : 'lg:left-(--sidebar-min) lg:max-w-[calc(100%_-_var(--sidebar-min))]']"
    >
      <div class="flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm font-medium mr-2">
        <span>Total Items: {{ totalItemsAcrossAllReports }}</span>
      </div>
      <div class="flex gap-3">
        <BaseButton
          text="Generate PDF"
          :icon="FilePdfIcon"
          size="sm"
          variant="secondary"
          :loading="isGeneratingPDF"
          :disabled="isGeneratingPDF || isSaving || isLoading"
          @click="generatePDF"
        />
        <BaseButton
          text="Save Report"
          :icon="SaveIcon"
          size="sm"
          variant="primary"
          :loading="isSaving"
          :disabled="isSaving || isLoading"
          @click="saveReport"
        />
      </div>
    </div>

    <!-- Loading Overlay -->
    <LoaderDefault v-if="isLoading" title="Loading report data..." customClasses="loaderFixed" />

    <div class="space-y-4">
      <!-- Select Won Tender + Company -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CardComponent title="Select Won Tender" :showHeaderBorder="true" :showHeaderPaddingBottom="true" bodyClass="flex-col">
          <template #body>
            <SelectOptionPrime
              id="selectWonTender" name="selectWonTender"
              :options="[{ label: 'Select Won Tender', value: '' }, ...wonTenders.map(tender => ({
                label: tender.referenceNo ? `${tender.referenceNo} - ${tender.tenderBrief?.substring(0, 50) || 'No description'}` : tender.t247Id || 'Unknown Tender',
                value: tender.t247Id || tender.id
              }))]"
              class="w-full" v-model="formData.selectedTender"
              @update:modelValue="handleTenderChange" :loading="isLoadingTenders"
            />
            <p v-if="wonTenders.length === 0 && !isLoadingTenders" class="text-xs text-amber-600 mt-1">No won tenders found</p>
          </template>
        </CardComponent>

        <CardComponent title="Company" :showHeaderBorder="true" :showHeaderPaddingBottom="true" bodyClass="flex-col">
          <template #body>
            <div class="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
              <BuildingIcon class="w-5 h-5 text-blue-600" />
              <span class="text-sm font-medium text-blue-800">{{ formData.company || 'No company assigned' }}</span>
            </div>
          </template>
        </CardComponent>
      </div>

      <!-- Details Form -->
      <CardComponent title="Details Form" :showHeaderBorder="true" :showHeaderPaddingBottom="true" headerClass="border-l-4 border-l-blue-500 dark:border-l-blue-500 sticky top-[60px]" bodyClass="grid grid-cols-3 gap-4">
        <template #body>
          <TextInputDefault id="referenceNumber" labelText="Reference Number" type="text" :isReadOnly="true" v-model="formData.referenceNumber" />
          <TextInputDefault id="purchaseOrderNumber" labelText="Purchase Order Number" type="text" v-model="formData.purchaseOrderNumber" />
          <TextInputDefault id="date" labelText="Date" type="date" v-model="date" :showIcon="true" placeholder="Select date" />
          <TextInputDefault id="poDate" labelText="PO Date" type="date" v-model="formData.poDate" :showIcon="true" placeholder="Select date" />
          <TextInputDefault id="customerName" labelText="Customer Name" type="text" v-model="formData.customerName" />
          <TextInputDefault id="customerAddress" labelText="Customer Address" type="text" v-model="formData.customerAddress" />
          <TextInputDefault id="stateOffice" labelText="State Office" type="text" v-model="formData.stateOffice" />
          <TextInputDefault id="contactPersonName" labelText="Contact Person Name" type="text" v-model="formData.contactPersonName" />
          <TextInputDefault id="contactPersonDesignation" labelText="Contact Person Designation" type="text" v-model="formData.contactPersonDesignation" />
          <TextInputDefault id="contactPersonNumber" labelText="Contact Person Number" type="text" v-model="formData.contactPersonNumber" />
          <TextInputDefault id="department" labelText="Department" type="text" v-model="formData.department" />
        </template>
      </CardComponent>

      <!-- Consignee Cards -->
      <div class="space-y-4">
        <div v-for="(consignee, consigneeIndex) in formData.consignees" :key="consignee.id" class="relative">
          <CardComponent
            :title="`Consignee #${consigneeIndex + 1}${consignee.name ? ' - ' + consignee.name : ''}`"
            :subTitle="`Assigned Engineer : ${consignee.assignedEngineerName}`"
            :showHeaderBorder="true" :showHeaderPaddingBottom="true"
            headerClass="border-l-4 border-l-red-500 dark:border-l-red-500 sticky top-[60px]"
            footerClass="justify-end"
          >
            <template #header>
              <div class="flex gap-2">
                <CheckBoxDefault
                  :modelValue="copyFromDetails[consignee.id] || false"
                  @update:modelValue="(checked) => handleCopyCheckboxChange(consignee.id, checked)"
                  label="Same as Contact Person Details" size="sm"
                  :disabled="!formData.contactPersonName && !formData.contactPersonDesignation && !formData.contactPersonNumber"
                />
                <BaseButton v-if="formData.consignees.length > 1" variant="danger" @click="removeConsignee(consignee.id)" :icon="TrashIcon" size="xs" tooltip="Remove Consignee" />
              </div>
            </template>

            <template #body>
              <div class="space-y-4 w-full">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <TextInputDefault :id="`consigneeName_${consignee.id}`" labelText="Consignee Name" type="text" v-model="consignee.name" />
                  <TextInputDefault :id="`consigneeDesignation_${consignee.id}`" labelText="Designation" type="text" v-model="consignee.designation" />
                  <TextInputDefault :id="`consigneeNumber_${consignee.id}`" labelText="Contact Number" type="text" v-model="consignee.number" />
                  <TextInputDefault :id="`consigneeCallDate_${consignee.id}`" labelText="Call Aligned Date" type="date" v-model="consignee.callAlignedDate" :showIcon="true" placeholder="Select date" />
                  <TextInputDefault :id="`consigneeEmail_${consignee.id}`" labelText="Email ID" type="email" v-model="consignee.emailId" placeholder="Enter email" />
                  <TextInputDefault :id="`consigneeFax_${consignee.id}`" labelText="FAX Number" type="text" v-model="consignee.faxNumber" placeholder="Enter FAX number" />
                </div>
                <div class="grid grid-cols-1 gap-4">
                  <SelectOptionPrime
                    :id="`assignedEngineer_${consignee.id}`" labelText="Assign Engineer"
                    :options="[{ label: 'Select Engineer', value: '' }, ...engineers.map(eng => {
                      const fullName = [eng.first_name || '', eng.last_name || ''].join(' ').trim()
                      return { label: fullName || eng.username || eng.email || 'Unknown Engineer', value: eng.id }
                    })]"
                    v-model="consignee.assignedEngineerId"
                    @update:modelValue="(value) => updateConsigneeEngineer(consignee, value)"
                    :loading="isLoadingEngineers"
                  />
                </div>
                <div v-if="consignee.assignedEngineerName" class="p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2">
                  <EngineerIcon class="w-4 h-4 text-green-600" />
                  <span class="text-xs text-green-700">Assigned to: {{ consignee.assignedEngineerName }}</span>
                </div>
              </div>
            </template>

            <template #footer>
              <BaseButton text="Add Report Template" :icon="PlusIcon" size="sm" variant="secondary" @click="openAddTemplateModal(consigneeIndex)" />
            </template>
          </CardComponent>

          <!-- Report Templates for this Consignee -->
          <div class="mt-4 space-y-4" v-if="consignee.reportCards.length > 0">
            <div v-for="(card, cardIndex) in consignee.reportCards" :key="card.id" class="relative">
              <CardComponent
                :title="`${card.title} (${card.itemGroups.length}/${totalItemsAcrossAllReports} Total Items)`"
                :subTitle="`Consignee #${consigneeIndex + 1}${consignee.name ? ' - ' + consignee.name : ''} | Assigned Engineer : ${consignee.assignedEngineerName}`"
                :showHeaderBorder="true" :showHeaderPaddingBottom="true"
                footerClass="justify-end"
                headerClass="border-l-4 border-l-green-500 dark:border-l-green-500 sticky top-[60px]"
                size="sm"
              >
                <template #header>
                  <BaseButton v-if="consignee.reportCards.length > 1" variant="danger" @click="removeReportCard(consigneeIndex, card.id)" :icon="TrashIcon" size="xs" tooltip="Remove Template" />
                </template>

                <template #body>
                  <div class="flex flex-col flex-1 gap-4">
                    <div class="w-full">
                      <TextAreaDefault
                        :id="`itemName_${consignee.id}_${card.id}`"
                        labelText="Item Name and Configuration" placeholder="Enter item details here..."
                        :modelValue="card.itemName"
                        @update:modelValue="(value) => updateItemName(consigneeIndex, cardIndex, value)"
                        :rows="3"
                      />
                    </div>

                    <!-- Warranty Fields -->
                    <div class="grid grid-cols-2 gap-4">
                      <SelectOptionPrime
                        :id="`warrantyYears_${consignee.id}_${card.id}`"
                        labelText="Warranty (Years)"
                        :options="warrantyYearsOptions"
                        v-model="card.warrantyYears"
                        size="sm"
                      />
                      <SelectOptionPrime
                        :id="`warrantyMonths_${consignee.id}_${card.id}`"
                        labelText="Warranty (Months)"
                        :options="warrantyMonthsOptions"
                        v-model="card.warrantyMonths"
                        size="sm"
                      />
                    </div>

                    <div class="w-full overflow-x-auto">
                      <DataTable :value="card.itemGroups" class="w-full text-sm" showGridlines stripedRows size="small"
                        :pt="{ root: { class: 'border border-gray-200 dark:border-gray-800 rounded-md' }, thead: { class: 'bg-gray-50 dark:bg-gray-800' }, th: { class: 'px-3 py-2 font-medium text-xs text-gray-700 dark:text-gray-300' }, td: { class: 'px-3 py-2' } }"
                      >
                        <Column v-if="card.visibleFields.monitorSerialNumber" field="fieldValues.monitorSerialNumber" header="Monitor Serial Number">
                          <template #body="{ data }">
                            <TextInputDefault :id="`monitorSerialNumber_${consignee.id}_${card.id}_${data.id}`" type="text" placeholder="Enter S/N" size="xs" :modelValue="data.fieldValues.monitorSerialNumber" @update:modelValue="(value) => updateFieldValue(consigneeIndex, cardIndex, data.id, 'monitorSerialNumber', value)" hideLabel />
                          </template>
                        </Column>
                        <Column v-if="card.visibleFields.cpuSerialNumber" field="fieldValues.cpuSerialNumber" header="CPU Serial Number">
                          <template #body="{ data }">
                            <TextInputDefault :id="`cpuSerialNumber_${consignee.id}_${card.id}_${data.id}`" type="text" placeholder="Enter S/N" size="xs" :modelValue="data.fieldValues.cpuSerialNumber" @update:modelValue="(value) => updateFieldValue(consigneeIndex, cardIndex, data.id, 'cpuSerialNumber', value)" hideLabel />
                          </template>
                        </Column>
                        <Column v-if="card.visibleFields.serialNumberCommon" field="fieldValues.serialNumberCommon" header="Serial Number (Common)">
                          <template #body="{ data }">
                            <TextInputDefault :id="`serialNumberCommon_${consignee.id}_${card.id}_${data.id}`" type="text" placeholder="Enter S/N" size="xs" :modelValue="data.fieldValues.serialNumberCommon" @update:modelValue="(value) => updateFieldValue(consigneeIndex, cardIndex, data.id, 'serialNumberCommon', value)" hideLabel />
                          </template>
                        </Column>
                        <Column v-if="card.visibleFields.installationStatus" field="fieldValues.installationStatus" header="Status">
                          <template #body="{ data }">
                            <SelectOptionPrime :id="`installationStatus_${consignee.id}_${card.id}_${data.id}`" :options="[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]" size="xs" placeholder="Select" :modelValue="data.fieldValues.installationStatus" @update:modelValue="(value) => updateFieldValue(consigneeIndex, cardIndex, data.id, 'installationStatus', value)" hideLabel />
                          </template>
                        </Column>
                        <Column v-if="card.visibleFields.remarks" field="fieldValues.remarks" header="Remarks">
                          <template #body="{ data }">
                            <TextInputDefault :id="`remarks_${consignee.id}_${card.id}_${data.id}`" type="text" placeholder="Enter remarks" size="xs" :modelValue="data.fieldValues.remarks" @update:modelValue="(value) => updateFieldValue(consigneeIndex, cardIndex, data.id, 'remarks', value)" hideLabel />
                          </template>
                        </Column>
                        <Column header="" headerStyle="width: 40px">
                          <template #body="{ data }">
                            <BaseButton v-if="card.itemGroups.length > 1" variant="dangernormal" @click="removeItemGroup(consigneeIndex, cardIndex, data.id)" :icon="TrashIcon" size="xs" />
                            <div v-else class="w-6"></div>
                          </template>
                        </Column>
                      </DataTable>
                    </div>
                  </div>
                </template>
                <template #footer>
                  <BaseButton text="Add Item" :icon="PlusIcon" size="xs" @click="addItemGroup(consigneeIndex, cardIndex)" />
                </template>
              </CardComponent>
            </div>
          </div>

          <div v-else class="mt-4 p-6 border-2 border-dashed border-gray-300 rounded-lg text-center">
            <FileWaveIcon class="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p class="text-sm text-gray-500">No report templates for this consignee yet.</p>
            <p class="text-xs text-gray-400 mt-1">Click "Add Report Template" above to create one.</p>
          </div>
        </div>

        <div class="flex justify-center">
          <BaseButton text="Add Another Consignee" :icon="PlusIcon" size="md" variant="primary" @click="addConsignee" />
        </div>
      </div>
    </div>
  </AdminLayout>

  <!-- Report Template Selection Modal -->
  <ModalDefault v-model="showModal" title="Select Report Template" width="max-w-lg" classBody="my-4">
    <ReportColumnSelectionModal v-if="activeConsigneeIndex !== null" ref="modalRef" @save="handleModalSave" @cancel="handleModalCancel" />
    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton text="Cancel" variant="secondary" @click="handleModalCancel" />
        <BaseButton text="Add Template" variant="primary" @click="handleModalSaveClick" />
      </div>
    </template>
  </ModalDefault>

  <!-- ── Consignee Selection Modal (shown when >1 consignee) ─────────────────── -->
  <ModalDefault v-model="showConsigneeSelectModal" title="Select Consignee for PDF" width="max-w-md" classBody="my-2">
    <div class="space-y-2">
      <!-- All Consignees option -->
      <div
        class="flex items-center gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-colors"
        :class="selectedConsigneeId === null
          ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700'
          : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-700'"
        @click="selectConsignee(null)"
      >
        <input type="radio" :checked="selectedConsigneeId === null" @click.stop class="accent-blue-600" />
        <div class="flex-1 min-w-0">
          <span class="text-sm font-medium text-gray-800 dark:text-gray-200">All Consignees</span>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Download a single PDF containing all {{ consigneesWithReports.length }} consignees
          </p>
        </div>
      </div>

      <!-- Divider -->
      <div class="flex items-center gap-2 py-1">
        <div class="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
        <span class="text-xs text-gray-400">or select one</span>
        <div class="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
      </div>

      <!-- Individual consignees -->
      <div
        v-for="(consignee, idx) in consigneesWithReports"
        :key="consignee.id"
        class="flex items-center gap-3 px-3 py-3 rounded-lg border cursor-pointer transition-colors"
        :class="selectedConsigneeId === consignee.id
          ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700'
          : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-700'"
        @click="selectConsignee(consignee.id)"
      >
        <input type="radio" :checked="selectedConsigneeId === consignee.id" @click.stop class="accent-blue-600" />
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">#{{ idx + 1 }}</span>
            <span class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
              {{ consignee.name || 'Unnamed Consignee' }}
            </span>
          </div>
          <div class="flex items-center gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            <span v-if="consignee.designation">{{ consignee.designation }}</span>
            <span v-if="consignee.assignedEngineerName" class="flex items-center gap-1">
              <EngineerIcon class="w-3 h-3" />
              {{ consignee.assignedEngineerName }}
            </span>
            <span class="text-blue-600 dark:text-blue-400">
              {{ consignee.reportCards.length }} template{{ consignee.reportCards.length !== 1 ? 's' : '' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <BaseButton text="Cancel" variant="secondary" @click="showConsigneeSelectModal = false" />
        <BaseButton
          :text="selectedConsigneeId === null ? `Download All (${consigneesWithReports.length})` : `Download Selected`"
          :icon="FilePdfIcon"
          variant="primary"
          @click="confirmConsigneeSelection"
        />
      </div>
    </template>
  </ModalDefault>
</template>
